/**
 * The report-window link, built from a form's local values.
 *
 * `datetime-local` gives a wall-clock string with no zone; the report takes the zone
 * separately, so the two are sent as they are and the server interprets the instants in the
 * named zone. The default window is the previous eight hours, which is what a morning report
 * is for.
 *
 * The timezone arithmetic itself lives in `@nightshift/ui-local-time`, shared with the
 * orchestrator so a preview and an enforced deadline cannot disagree. This file is only the
 * URL building.
 *
 * The endpoint keeps its `assisted-run` name: the *report* is retained history, and renaming a
 * route would break every saved link to it. Only the controls were retired.
 */
import { currentTimezone, localInZoneToIso } from '@nightshift/ui-local-time';

export interface ReportWindowInput {
  from: string;
  to: string;
  tz: string;
  project: string | null;
  format: 'md' | 'json' | 'csv';
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function local(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function defaultReportWindow(now: Date): { from: string; to: string; tz: string } {
  const to = new Date(now);
  to.setSeconds(0, 0);
  const from = new Date(to.getTime() - 8 * 60 * 60 * 1000);
  return { from: local(from), to: local(to), tz: currentTimezone() };
}

export function assistedRunReportUrl(input: ReportWindowInput): string {
  const params = new URLSearchParams({
    from: localInZoneToIso(input.from, input.tz),
    to: localInZoneToIso(input.to, input.tz),
    tz: input.tz,
    format: input.format,
  });
  if (input.project) params.set('project', input.project);
  return `/api/reports/assisted-run?${params.toString()}`;
}
